import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Sparkles,
  ArrowRight,
  Home,
  FileText,
  HelpCircle,
  Settings,
  Loader2,
  ChevronDown,
  ChevronRight,
  Trash2,
  Calendar,
  CreditCard,
  Users,
  Package,
  MessageSquare,
  Award,
  RotateCcw,
  Grid,
  Keyboard,
  Shield,
  Lock
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import toast from 'react-hot-toast';

const UnifiedChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('selection'); // 'typing' or 'selection' - Default to selection mode
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [currentStep, setCurrentStep] = useState('main');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize messages
  const getInitialMessage = () => ({
    id: 1,
    type: 'bot',
    content: "👋 Hi! I'm your SwiftFixPro assistant. Choose how you'd like to interact:\n\n🎯 **Selection Mode**: Quick access via buttons\n⌨️ **Typing Mode**: Free text chat",
    timestamp: new Date()
  });

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([getInitialMessage()]);
    }
  }, []);

  // Quick action buttons
  const quickActions = [
    { icon: Home, label: 'Dashboard', action: 'navigate:/dashboard', color: 'bg-orange-500' },
    { icon: FileText, label: 'My Orders', action: 'navigate:/jobs', color: 'bg-orange-600' },
    { icon: HelpCircle, label: 'FAQs', action: 'navigate:/faq', color: 'bg-red-500' },
    { icon: Settings, label: 'Settings', action: 'navigate:/profile', color: 'bg-orange-700' },
  ];

  // Conversation tree for selection mode
  const conversationTree = {
    main: {
      botMessage: "👋 Welcome to SwiftFixPro! What would you like to know about?",
      options: [
        {
          id: 'services_info',
          icon: Package,
          label: 'Services',
          description: 'Explore our service categories',
          nextStep: 'services_info'
        },
        {
          id: 'payment_info',
          icon: CreditCard,
          label: 'Payment',
          description: 'Payment methods & transactions',
          nextStep: 'payment_info'
        },
        {
          id: 'privacy_security',
          icon: Shield,
          label: 'Privacy & Security',
          description: 'Data protection & privacy policy',
          nextStep: 'privacy_security'
        },
        {
          id: 'customer_service',
          icon: MessageSquare,
          label: 'Customer Service',
          description: 'Support & assistance',
          nextStep: 'customer_service'
        },
        {
          id: 'booking_info',
          icon: Calendar,
          label: 'Booking & Orders',
          description: 'How to book and manage orders',
          nextStep: 'booking_info'
        },
        {
          id: 'faqs',
          icon: HelpCircle,
          label: 'FAQs',
          description: 'Frequently asked questions',
          nextStep: 'faqs'
        }
      ]
    },
    services_info: {
      botMessage: "🔧 **SwiftFixPro Services:**\n\nWe offer 13 comprehensive service categories:\n\n🏠 **Home Repairs** - General repairs, fixture installation, hardware replacement, door & window repairs\n\n🎨 **Painting** - Interior & exterior painting, wall preparation, color consultation\n\n⚡ **Electrical** - Wiring, circuit breaker repair, outlet & light fixture installation (Same day response)\n\n🚰 **Plumbing** - Pipe repair, faucet & toilet installation, water heater service (1 hour emergency response)\n\n🪑 **Carpentry** - Custom furniture, cabinet installation, shelving solutions\n\n🏠 **Flooring** - Hardwood, tile, carpet installation & repair\n\n📦 **Appliance Installation** - Kitchen appliances, washing machine, dryer setup\n\n🪑 **Furniture Assembly** - IKEA furniture, office & bedroom furniture assembly\n\n🚚 **Moving Services** - Residential & office moving, packing services\n\n🏗️ **Renovation** - Kitchen & bathroom remodeling, complete home renovation\n\n🔒 **Safety & Security** - Security system, camera & alarm installation\n\n🧹 **Cleaning** - Regular house cleaning, deep cleaning, post-construction cleanup\n\n💙 **Elderly Home Care** - Health monitoring, home maintenance, daily check-ins, 24-hour AI camera monitoring, Fitbit emergency response",
      options: []
    },
    booking_info: {
      botMessage: "📅 **How to Book on SwiftFixPro:**\n\n**Step-by-Step Process:**\n1️⃣ Choose your service from 13 categories\n2️⃣ Fill in service details & description\n3️⃣ Enter your Singapore property location\n4️⃣ Select preferred date & time slot\n5️⃣ Upload photos/videos (optional but helpful)\n6️⃣ Add special instructions if needed\n7️⃣ Submit order & make payment\n8️⃣ Receive vendor assignment notification\n\n**Default Booking:**\n• Date: Any available date\n• Time: 9:00 AM - 12:00 PM\n• Duration: 2 hours estimated\n• Emergency option available for urgent issues",
      options: [
        {
          id: 'booking_tips',
          icon: HelpCircle,
          label: 'Booking Tips',
          description: 'Best practices for booking',
          nextStep: 'booking_tips'
        }
      ]
    },
    booking_tips: {
      botMessage: "💡 **Booking Tips:**\n\n📸 **Upload Photos/Videos**\nClear images help vendors provide accurate quotes\n\n📝 **Detailed Description**\nDescribe the issue thoroughly in the description field\n\n📍 **Complete Address**\nInclude street address, building, unit number, and postal code\n\n⚡ **Emergency Service**\nToggle emergency option for urgent issues\n\n🏢 **Access Instructions**\nProvide gate codes, parking info, or entry instructions\n\n☎️ **Contact Number**\nEnsure your phone number is correct for vendor contact",
      options: []
    },
    payment_info: {
      botMessage: "💰 **Payment Information:**\n\n**Payment Process:**\n1️⃣ Vendors review your order and provide quotes\n2️⃣ You receive quote notification via email/SMS\n3️⃣ Review and approve the quote\n4️⃣ Make payment through secure **HitPay gateway**\n5️⃣ Vendor starts work after payment confirmed\n\n**Payment Methods:**\n💳 **Credit/Debit Cards** (via HitPay secure gateway)\n• Visa, MasterCard, American Express\n• Secure 3D authentication\n• Instant payment confirmation\n\n**Payment Security:**\n🔒 PCI DSS compliant payment processing\n🔐 SSL encrypted transactions\n✅ No card details stored on our servers\n\n**Service Warranty:**\n• Home Repairs: 12 months\n• Painting: 24 months\n• Electrical: 18 months\n• Plumbing: 12 months\n• Carpentry: 36 months\n• Renovation: 5 years\n\n**Payment Retention:**\n⏳ SwiftFixPro may withhold a portion of payments for 30 days to manage risks of defects, disputes, or claims.\n\n💳 All payments are processed securely through HitPay payment gateway.",
      options: [
        {
          id: 'cancellation_refund',
          icon: RotateCcw,
          label: 'Cancellation & Refunds',
          description: 'Cancellation policy & refund process',
          nextStep: 'cancellation_refund'
        },
        {
          id: 'payment_issues',
          icon: HelpCircle,
          label: 'Payment Issues',
          description: 'Having trouble with payment?',
          nextStep: 'payment_issues'
        }
      ]
    },
    cancellation_refund: {
      botMessage: "🔄 **Cancellation & Refund Policy:**\n\n**User Cancellation:**\n\n✅ **48+ Hours Before Service:**\n• Full refund issued\n• Platform fee deducted\n• No cancellation penalty\n\n⚠️ **24-48 Hours Before Service:**\n• Cancellation fee: 30% of service cost\n• Platform fee deducted\n• Remaining amount refunded\n\n❌ **Less than 24 Hours / No-Show:**\n• No refund for deposit\n• Platform fee deducted\n• Service deemed completed\n• Rescheduling option: +SGD $50 transport fee\n\n**Rescheduling Policy:**\n\n📅 **First Reschedule:**\n• Free of charge\n• Contact vendor via Messages page\n• Subject to vendor availability\n\n📅 **Second Reschedule:**\n• Treated as NEW booking\n• Original booking deemed completed\n• Payments non-transferable\n• Full service fees apply for new booking\n\n**Service Complaints & Refunds:**\n\n⏰ **Reporting Window:**\n• Must report within 7 days of service completion\n• Late reports may not be eligible for refund\n\n🔧 **Resolution Options:**\n• Re-performance of service\n• Partial refund (at SwiftFixPro's discretion)\n• Service credits\n• Complies with Consumer Protection (Fair Trading) Act\n\n**Refund Processing:**\n💰 Approved refunds processed within 5-7 business days\n💳 Refunded to original payment method\n⏳ Bank processing may take additional 3-5 days\n\n**How to Cancel:**\n1. Go to 'My Orders'\n2. Select the booking\n3. Click 'Request Cancellation'\n4. Confirm cancellation\n5. Refund processed automatically based on policy\n\n📞 **Need Help?**\nContact: +65 8861 3196 or sales@swiftfixpro.com",
      options: []
    },
    payment_issues: {
      botMessage: "🔧 **Payment Troubleshooting:**\n\n**Common Issues:**\n\n❌ **Payment Declined**\n• Check card details are correct\n• Ensure sufficient funds available\n• Contact your bank for authorization\n• Try alternative payment method\n\n⏰ **Payment Pending**\n• Wait 5-10 minutes for confirmation\n• Check your email for payment status\n• Contact support if delayed beyond 1 hour\n\n🔄 **Refund Status**\n• Refunds process within 5-7 business days\n• Check with your bank for posting time\n• Contact support for refund tracking\n\n💬 **Need Help?**\nContact customer service:\n📞 +65 8861 3196\n📧 sales@swiftfixpro.com",
      options: []
    },
    privacy_security: {
      botMessage: "🔒 **Privacy & Security Policy:**\n\n**Data Protection:**\n🛡️ **Personal Information**\n• Your data is encrypted and securely stored\n• We comply with PDPA (Personal Data Protection Act)\n• No data sharing with third parties without consent\n• Regular security audits and updates\n\n**What We Collect:**\n📋 **Account Information**\n• Name, email, phone number\n• Address for service delivery\n• Payment information (securely via HitPay)\n\n🔐 **Security Measures:**\n• SSL/TLS encryption for all data transmission\n• Secure password hashing (bcrypt)\n• Two-factor authentication available\n• Regular security vulnerability assessments\n• PCI DSS compliant payment processing\n\n**Your Rights:**\n✅ Access your personal data\n✅ Request data correction or deletion\n✅ Withdraw consent for data processing\n✅ Export your data\n\n**Cookie Policy:**\n🍪 We use cookies to:\n• Maintain your login session\n• Remember your preferences\n• Analyze website traffic\n• Improve user experience\n\n**Data Retention:**\n• Active account data retained while account exists\n• Deleted account data purged within 30 days\n• Order history retained for 7 years (legal requirement)\n\n📄 **Full Privacy Policy:**\nAvailable at www.swiftfixpro.com/privacy\n\n**Questions or Concerns?**\nContact our Data Protection Officer:\n📧 privacy@swiftfixpro.com",
      options: [
        {
          id: 'account_security',
          icon: Lock,
          label: 'Account Security',
          description: 'How to secure your account',
          nextStep: 'account_security'
        }
      ]
    },
    account_security: {
      botMessage: "🔐 **Account Security Tips:**\n\n**Strong Password:**\n✅ Use at least 12 characters\n✅ Mix uppercase, lowercase, numbers & symbols\n✅ Avoid common words or personal info\n✅ Use unique password for SwiftFixPro\n\n**Security Best Practices:**\n🔒 **Enable Two-Factor Authentication** (Coming soon)\n👀 **Monitor Account Activity**\n• Check login history regularly\n• Review order history for unauthorized bookings\n• Verify email notifications\n\n⚠️ **Protect Your Account:**\n• Never share your password\n• Logout from shared devices\n• Be cautious of phishing emails\n• Keep your contact info updated\n\n**Suspicious Activity?**\n🚨 Report immediately:\n📧 security@swiftfixpro.com\n📞 +65 8861 3196\n\n**Account Recovery:**\n• Use 'Forgot Password' to reset\n• Verify via registered email/phone\n• Contact support if unable to access",
      options: []
    },
    customer_service: {
      botMessage: "💬 **Customer Service & Support:**\n\n**Contact Us:**\n📱 **Phone Support:**\n+65 8861 3196\n🕒 Monday to Sunday: 8:00 AM - 10:00 PM\n\n📧 **Email Support:**\nsales@swiftfixpro.com\n⏱️ Response within 24 hours\n\n📍 **Office Location:**\nBlk 625 Aljunied Road #05-08\nAljunied Industrial Complex\nSingapore 389836\n\n**Support Channels:**\n💬 **Live Chat** - Available during business hours\n📱 **Messages** - Chat with assigned vendors\n📧 **Email** - For detailed inquiries\n☎️ **Phone** - For urgent matters\n\n**What We Can Help With:**\n✅ Order status inquiries\n✅ Booking assistance\n✅ Payment issues\n✅ Vendor concerns\n✅ Service quality feedback\n✅ Account management\n✅ Technical support\n\n**Response Times:**\n🚨 **Emergency Issues:** Within 1 hour\n⚡ **Urgent Matters:** Within 4 hours\n📋 **General Inquiries:** Within 24 hours\n\n**Quality Assurance:**\n⭐ All customer service interactions are monitored for quality\n📊 Customer satisfaction is our priority\n🎯 We aim to resolve 95% of issues on first contact",
      options: [
        {
          id: 'feedback_complaints',
          icon: MessageSquare,
          label: 'Feedback & Complaints',
          description: 'Submit feedback or complaint',
          nextStep: 'feedback_complaints'
        }
      ]
    },
    feedback_complaints: {
      botMessage: "📝 **Feedback & Complaints:**\n\n**We Value Your Feedback!**\n\n**How to Submit:**\n\n⭐ **Service Feedback:**\n1. Go to 'My Orders'\n2. Select completed service\n3. Click 'Rate Service'\n4. Provide rating & review\n\n⚠️ **File a Complaint:**\n📧 Email: complaints@swiftfixpro.com\n📞 Phone: +65 8861 3196\n\n**Include in Your Complaint:**\n• Order number\n• Date of service\n• Vendor name\n• Detailed description of issue\n• Supporting photos/videos (if applicable)\n\n**Complaint Resolution Process:**\n1️⃣ Acknowledgment within 24 hours\n2️⃣ Investigation by quality team\n3️⃣ Communication with vendor\n4️⃣ Resolution proposal within 5 business days\n5️⃣ Follow-up to ensure satisfaction\n\n**Escalation:**\nIf unsatisfied with resolution:\n📧 escalation@swiftfixpro.com\n👤 Manager review within 3 business days\n\n**Your Rights:**\n✅ Fair investigation\n✅ Timely response\n✅ Appropriate compensation (if warranted)\n✅ Service quality guarantee",
      options: []
    },
    faqs: {
      botMessage: "❓ **Frequently Asked Questions:**\n\n**General Questions:**\n\n**Q: How long does it take to get a vendor assigned?**\nA: Typically within 2-4 hours during business hours. Emergency services within 1 hour.\n\n**Q: Can I cancel my order?**\nA: Yes, with conditions:\n• 48+ hours before service: Full refund minus platform fee\n• 24-48 hours before: 30% cancellation fee + platform fee\n• Less than 24 hours or no-show: No refund for deposit + platform fee\n\n**Q: What if I'm not satisfied with the service?**\nA: Contact us immediately. We offer service guarantee and will resolve issues or arrange for rework.\n\n**Q: Do vendors bring their own tools?**\nA: Yes, all vendors come equipped with necessary tools and materials unless otherwise specified.\n\n**Q: How do I track my order?**\nA: Log in to your dashboard and go to 'My Orders' to view real-time status updates.\n\n**Q: Can I request a specific vendor?**\nA: Yes, if you've worked with a vendor before, you can request them in the special instructions.\n\n**Q: What areas do you cover?**\nA: We cover all of Singapore. Service availability may vary by location.\n\n**Q: Are vendors insured?**\nA: Yes, all our vendors are insured and background-checked for your safety.\n\n**Q: How do I get a quote?**\nA: Submit your service request with details and photos. Vendors will provide quotes within 2-4 hours.\n\n**Q: Can I reschedule my appointment?**\nA: Yes, contact your assigned vendor through the Messages page to reschedule.",
      options: [
        {
          id: 'services_info',
          icon: Package,
          label: 'More About Services',
          description: 'Learn about our services',
          nextStep: 'services_info'
        }
      ]
    },
    help_options: {
      botMessage: "❓ **Help & Support:**\n\nHow can I assist you?",
      options: [
        {
          id: 'contact_support',
          icon: MessageSquare,
          label: 'Contact Support',
          description: 'Get in touch with us',
          nextStep: 'contact_support'
        },
        {
          id: 'account_help',
          icon: Settings,
          label: 'Account Help',
          description: 'Profile & settings',
          nextStep: 'account_help'
        }
      ]
    },
    contact_support: {
      botMessage: "📞 **Contact SwiftFixPro:**\n\n📱 **Phone Consultation:**\n+65 8861 3196\nMonday to Sunday: 8:00 AM - 10:00 PM\n\n📧 **Email Consultation:**\nsales@swiftfixpro.com\nResponse within 24 hours\n\n📍 **Office Address:**\nBlk 625 Aljunied Road #05-08\nAljunied Industrial Complex\nSingapore 389836\n\n💬 **Platform Support:**\n• Dashboard: Check order notifications\n• Messages: Chat with your assigned vendor\n• FAQ Page: Browse common questions\n\n🌟 **For service-related issues, contact your assigned vendor directly through the Messages page!**",
      options: []
    },
    account_help: {
      botMessage: "👤 **SwiftFixPro Account Help:**\n\n**Dashboard Features:**\n• View all your orders and status\n• Track ongoing services\n• Access past service history\n• Quick booking options\n\n**Profile Management:**\n• Update personal information\n• Change password\n• Manage contact details\n\n**My Orders:**\n• View order details\n• Track order status\n• Upload additional documents\n• Request cancellation (see policy)\n• Reschedule appointments\n\n**Messages:**\n• Chat with assigned vendors\n• View conversation history\n• Receive real-time updates\n\n**Referral Program:**\n• Share your referral code\n• Earn rewards for referrals\n• Track referral status",
      options: []
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Switch mode handler
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    const modeMessage = {
      id: Date.now(),
      type: 'bot',
      content: newMode === 'typing'
        ? "⌨️ Typing mode activated! Feel free to type your questions."
        : "🎯 Selection mode activated! Choose from the options below.",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, modeMessage]);

    if (newMode === 'selection') {
      setCurrentStep('main');
      const welcomeMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: conversationTree.main.botMessage,
        timestamp: new Date()
      };
      setTimeout(() => {
        setMessages(prev => [...prev, welcomeMessage]);
      }, 300);
    }
  };

  // Typing mode: Send message
  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await api.post('/chatbot/message', {
        message: messageText,
        context: {
          currentPage: location.pathname,
          timestamp: new Date().toISOString()
        }
      });

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.message,
        action: response.action,
        suggestions: response.suggestions,
        timestamp: new Date()
      };

      setTimeout(() => {
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);

        if (response.action) {
          handleBotAction(response.action);
        }
      }, 800);

    } catch (error) {
      console.error('Chatbot error:', error);

      const fallbackResponse = generateFallbackResponse(messageText);
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: fallbackResponse.message,
        action: fallbackResponse.action,
        timestamp: new Date()
      };

      setTimeout(() => {
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);

        if (fallbackResponse.action) {
          handleBotAction(fallbackResponse.action);
        }
      }, 800);
    }
  };

  // Selection mode: Handle option click
  const handleOptionClick = (option) => {
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: option.label,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    if (option.nextStep) {
      setCurrentStep(option.nextStep);

      const nextStepData = conversationTree[option.nextStep];
      if (nextStepData) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: nextStepData.botMessage,
          timestamp: new Date()
        };

        setTimeout(() => {
          setMessages(prev => [...prev, botMessage]);
        }, 300);
      }
    }
  };

  const generateFallbackResponse = (message) => {
    const lowerMessage = message.toLowerCase().trim();

    const navigationMap = {
      'dashboard': { path: '/dashboard', name: 'Dashboard' },
      'orders': { path: '/jobs', name: 'Orders' },
      'services': { path: '/services', name: 'Services' },
      'profile': { path: '/profile', name: 'Profile' },
      'messages': { path: '/messages', name: 'Messages' },
    };

    for (const [keyword, navInfo] of Object.entries(navigationMap)) {
      if (lowerMessage.includes(keyword)) {
        return {
          message: `Taking you to ${navInfo.name}! 🚀`,
          action: { type: 'navigate', path: navInfo.path }
        };
      }
    }

    return {
      message: "I can help you with:\n\n🏠 Navigation\n📋 Booking services\n❓ FAQs\n⚙️ Account settings\n\nWhat would you like to know?",
      action: null
    };
  };

  const handleBotAction = (action) => {
    if (!action) return;

    if (action.type === 'navigate') {
      setTimeout(() => {
        navigate(action.path);
        toast.success(`Navigating to ${action.path}`);
      }, 1000);
    }
  };

  const handleQuickAction = (action) => {
    if (action.startsWith('navigate:')) {
      const path = action.replace('navigate:', '');
      navigate(path);
      toast.success(`Navigating to ${path}`);
      setIsOpen(false);
    }
  };

  const clearChat = () => {
    setMessages([getInitialMessage()]);
    setCurrentStep('main');
    setShowQuickActions(true);
    toast.success('Chat cleared');
  };

  const handleBackToMain = () => {
    setCurrentStep('main');
    const botMessage = {
      id: Date.now(),
      type: 'bot',
      content: conversationTree.main.botMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const currentStepData = conversationTree[currentStep] || conversationTree.main;

  return (
    <>
      {/* Floating Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-5 rounded-full shadow-2xl hover:shadow-orange-500/50 transition-all duration-300"
          >
            <div className="relative">
              <MessageCircle className="w-8 h-8" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse"></span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-8 right-8 z-50 w-[600px] h-[800px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Bot className="w-7 h-7 text-orange-600" />
                  </div>
                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white"></span>
                </div>
                <div>
                  <h3 className="font-bold text-xl">AI Assistant</h3>
                  <p className="text-sm text-orange-100">Online • Ready to help</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearChat}
                  className="hover:bg-white/20 p-2 rounded-lg transition-colors"
                  title="Clear chat"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Mode Switcher - HIDDEN: Only selection mode available */}
            {/* <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 border-b border-gray-200">
              <div className="flex gap-2">
                <button
                  onClick={() => handleModeSwitch('typing')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    mode === 'typing'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Keyboard className="w-4 h-4" />
                  Typing Mode
                </button>
                <button
                  onClick={() => handleModeSwitch('selection')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    mode === 'selection'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  Selection Mode
                </button>
              </div>
            </div> */}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-gray-50">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex gap-3 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                        : 'bg-gradient-to-r from-orange-400 to-red-500'
                    }`}>
                      {message.type === 'user' ? (
                        <User className="w-5 h-5 text-white" />
                      ) : (
                        <Bot className="w-5 h-5 text-white" />
                      )}
                    </div>

                    <div>
                      <div className={`rounded-2xl px-5 py-4 ${
                        message.type === 'user'
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                          : 'bg-gradient-to-br from-white to-orange-50 text-gray-800 shadow-md border-2 border-orange-100'
                      }`}>
                        <div className="text-base whitespace-pre-line leading-relaxed space-y-2">
                          {message.content.split('\n').map((line, idx) => {
                            // Check if line contains markdown bold **text**
                            if (line.includes('**')) {
                              const parts = line.split(/(\*\*.*?\*\*)/g);
                              return (
                                <p key={idx} className="my-1">
                                  {parts.map((part, partIdx) => {
                                    if (part.startsWith('**') && part.endsWith('**')) {
                                      return (
                                        <span key={partIdx} className="font-bold text-orange-600">
                                          {part.slice(2, -2)}
                                        </span>
                                      );
                                    }
                                    return <span key={partIdx}>{part}</span>;
                                  })}
                                </p>
                              );
                            }
                            // Check if line starts with emoji or bullet
                            if (line.match(/^[🔧⚡🚰🏠🎨🪑📦🚚🏗️🔒🧹💙✅📅💰🔒❓📦🔄📞👤💡]/)) {
                              return <p key={idx} className="font-semibold text-gray-900 my-1">{line}</p>;
                            }
                            // Regular lines with bullet points
                            if (line.trim().startsWith('•')) {
                              return <p key={idx} className="text-gray-700 ml-2 my-0.5">{line}</p>;
                            }
                            // Number steps
                            if (line.match(/^[0-9]️⃣/)) {
                              return <p key={idx} className="font-medium text-gray-800 my-1">{line}</p>;
                            }
                            return <p key={idx}>{line}</p>;
                          })}
                        </div>
                      </div>

                      {message.suggestions && message.suggestions.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {message.suggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => mode === 'typing' && handleSendMessage(suggestion)}
                              className="text-sm text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-1"
                            >
                              <ArrowRight className="w-4 h-4" />
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}

                      <p className="text-xs text-gray-400 mt-2">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="bg-white rounded-2xl px-5 py-4 shadow-sm border border-gray-200">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Selection Mode Options */}
            {mode === 'selection' && (
              <div className="p-4 bg-gradient-to-br from-orange-50 to-white border-t-2 border-orange-200 max-h-[280px] overflow-y-auto">
                <p className="text-sm font-bold text-orange-600 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Choose an option:
                </p>
                <div className="space-y-2">
                  {currentStepData.options.map((option) => (
                    <motion.button
                      key={option.id}
                      whileHover={{ scale: 1.02, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionClick(option)}
                      className="w-full flex items-center gap-3 p-4 bg-white hover:bg-gradient-to-r hover:from-orange-500 hover:to-orange-600 border-2 border-orange-200 hover:border-orange-500 rounded-xl transition-all group shadow-sm hover:shadow-lg"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 group-hover:from-white group-hover:to-white rounded-xl flex items-center justify-center flex-shrink-0 transition-all shadow-sm">
                        <option.icon className="w-6 h-6 text-orange-600 group-hover:text-orange-600 transition-colors" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-gray-800 group-hover:text-white text-base transition-colors">{option.label}</p>
                        {option.description && (
                          <p className="text-xs text-gray-500 group-hover:text-orange-100 transition-colors mt-0.5">{option.description}</p>
                        )}
                      </div>
                      <ChevronRight className="w-6 h-6 text-orange-400 group-hover:text-white transition-colors" />
                    </motion.button>
                  ))}

                  {currentStep !== 'main' && (
                    <button
                      onClick={handleBackToMain}
                      className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 border-2 border-gray-300 rounded-xl transition-all text-sm font-semibold text-gray-700 shadow-sm hover:shadow-md mt-3"
                    >
                      <Home className="w-4 h-4" />
                      <span>Back to Main Menu</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Typing Mode Input - HIDDEN: Only selection mode available */}
            {/* {mode === 'typing' && (
              <div className="p-5 bg-white border-t border-gray-200">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-5 py-4 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputMessage.trim()}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-sm text-gray-400 mt-3 text-center">
                  Powered by SwiftFixPro AI ✨
                </p>
              </div>
            )} */}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default UnifiedChatbot;
